import {
  AudioConfig,
  AudioOutputStream,
  PropertyId,
  ResultReason,
  SpeechConfig,
  SpeechSynthesizer,
  SpeechSynthesisOutputFormat,
} from "microsoft-cognitiveservices-speech-sdk";
import { getAzureToken } from "./interviewService";
import {
  token,
  region,
  setToken,
  setRegion,
  setPlayState,
} from "../../stores/userStore";

let currentSynthesizer = null;
let currentAudioContext = null;

export async function synthesizeSpeech(question) {
  if (!token() || !region()) {
    const { token, region } = await getAzureToken();
    setToken(token);
    setRegion(region);
  }

  if (currentSynthesizer) {
    try {
      currentSynthesizer.close();
    } catch (e) {
      console.warn("Error closing previous synthesizer:", e);
    }
    currentSynthesizer = null;
  }

  if (currentAudioContext) {
    try {
      await currentAudioContext.close();
    } catch (e) {
      console.warn("Error closing previous audio context:", e);
    }
    currentAudioContext = null;
  }

  return new Promise((resolve, reject) => {
    const SPEECH_TOKEN = token();
    const SPEECH_REGION = region();

    if (!SPEECH_TOKEN || !SPEECH_REGION) {
      return reject(new Error("Speech token or region is not set."));
    }

    const speechConfig = SpeechConfig.fromAuthorizationToken(
      SPEECH_TOKEN,
      SPEECH_REGION,
    );
    speechConfig.speechSynthesisVoiceName = "en-US-AvaMultilingualNeural";
    speechConfig.setProperty(
      PropertyId.SpeechSynthesisOutputFormat,
      SpeechSynthesisOutputFormat.Audio16Khz128KBitRateMonoMp3,
    );

    // Use null stream to suppress auto playback in browser
    const stream = AudioOutputStream.createPullStream();
    const audioConfig = AudioConfig.fromStreamOutput(stream);
    const synthesizer = new SpeechSynthesizer(speechConfig, audioConfig);
    currentSynthesizer = synthesizer;

    synthesizer.speakTextAsync(
      question,
      (result) => {
        currentSynthesizer = null;

        if (result.reason === ResultReason.SynthesizingAudioCompleted) {
          const blob = new Blob([result.audioData], { type: "audio/mp3" });
          const audioUrl = URL.createObjectURL(blob);
          const audio = new Audio(audioUrl);
          playAndAnalyzeAudio(audio, resolve, reject);
        } else {
          console.error("Speech synthesis failed:", result.errorDetails);
          window.dispatchEvent(new CustomEvent("speech-end"));
          reject(new Error(result.errorDetails));
        }

        try {
          synthesizer.close();
        } catch (e) {
          console.warn("Error closing synthesizer:", e);
        }
      },
      (err) => {
        currentSynthesizer = null;
        console.error("Speech synthesis error:", err);
        window.dispatchEvent(new CustomEvent("speech-end"));
        try {
          synthesizer.close();
        } catch (e) {
          console.warn("Error closing synthesizer:", e);
        }
        reject(err);
      },
    );
  });
}

function playAndAnalyzeAudio(audio, resolve, reject) {
  const context = new AudioContext();
  currentAudioContext = context;

  const resumeContext = async () => {
    if (context.state === "suspended") {
      await context.resume();
    }
  };

  const source = context.createMediaElementSource(audio);
  const analyser = context.createAnalyser();
  source.connect(analyser);
  analyser.connect(context.destination);

  const dataArray = new Uint8Array(analyser.frequencyBinCount);

  function update() {
    analyser.getByteTimeDomainData(dataArray);
    const normalized =
      dataArray.reduce((a, b) => a + Math.abs(b - 128), 0) /
      dataArray.length /
      128;
    if (window.MiaAPI?.updateAmplitude) {
      window.MiaAPI.updateAmplitude(normalized);
    }
    if (!audio.paused && !audio.ended) {
      requestAnimationFrame(update);
    }
  }

  audio.oncanplaythrough = async () => {
    try {
      await resumeContext();
      window.dispatchEvent(new CustomEvent("speech-start"));
      window.MiaAPI?.startSpeaking?.();
      await audio.play();
      update();
      resolve();
    } catch (err) {
      console.error("AudioContext resume or play failed:", err);
      window.dispatchEvent(new CustomEvent("speech-end"));
      reject(err);
    }
  };

  audio.onended = async () => {
    window.MiaAPI?.stopSpeaking?.();
    window.dispatchEvent(new CustomEvent("speech-end"));
    try {
      source.disconnect();
      analyser.disconnect();
      await context.close();
      currentAudioContext = null;
      setPlayState(false);
    } catch (err) {
      console.warn("Cleanup error after audio ended:", err);
    }
  };

  audio.onerror = (e) => {
    console.error("Audio error:", e);
    window.dispatchEvent(new CustomEvent("speech-end"));
    reject(new Error("Audio playback failed"));
  };
}

export function stopSpeech() {
  return new Promise((resolve) => {
    // Stop the synthesizer if it exists
    if (currentSynthesizer) {
      try {
        currentSynthesizer.close();
        currentSynthesizer = null;
      } catch (e) {
        console.warn("Error closing synthesizer:", e);
      }
    }

    // Stop audio context and any playing audio elements
    if (currentAudioContext) {
      try {
        // Disconnect any audio nodes if they exist
        // This will effectively stop audio output
        currentAudioContext.close().catch((e) => {
          console.warn("Error closing audio context:", e);
        });
        currentAudioContext = null;
      } catch (e) {
        console.warn("Error handling audio context:", e);
      }
    }

    // Update UI state
    setPlayState(false);

    // Notify the MiaAPI if it exists
    if (window.MiaAPI?.stopSpeaking) {
      window.MiaAPI.stopSpeaking();
    }

    resolve();
  });
}
